import type { PosCollection, PosSale, PosSession, PosZReport } from '@erp/shared';
import { Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import { Badge, Button, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDateTime } from '../../lib/intl.js';

import { sessionBreakdown, sessionDrawerCards, sessionSaleRows } from './shiftBreakdown.js';
import { buildShiftPrintHtml, printHtml } from './shiftReport.js';
import {
  DrawerCards, NoteBox, PaymentMethods, SalesByCurrency, ShiftTiles,
  useDuration, useShiftMoney, useTodayRates,
} from './ShiftView.js';

/**
 * One WEB-REGISTER shift — open or closed — opened from the close-outs list,
 * drawn by ShiftView exactly like a till close-out (TerminalShiftDialog): the
 * money per currency, where it came from, and the sales split IQD / USD.
 *
 * The drawer figures come from the shift's Z-report (per currency: cash
 * received incl. notes taken for sales in other currencies, change, refunds,
 * expected — and, once closed, counted and the difference). A viewer without
 * the right to read it still gets the sales; the cards say nothing rather than
 * guess.
 */
export function SessionSalesDialog({ session, onClose }: {
  session: PosSession | null;
  onClose: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation('pos');
  const { t: tc } = useTranslation();
  const tenant = useAuthStore((s) => s.tenant);
  const money = useShiftMoney();
  const duration = useDuration();
  const [sales, setSales] = useState<PosSale[] | null>(null);
  const [z, setZ] = useState<PosZReport | null | 'none'>(null);
  /** Customers who paid off their debt in this shift. */
  const [collections, setCollections] = useState<PosCollection[]>([]);
  const rates = useTodayRates(session?.currencyCode ?? null);

  const id = session?.id ?? null;
  useEffect(() => {
    if (!id) { setSales(null); setZ(null); return; }
    let on = true;
    setSales(null);
    setZ(null);
    void api()
      .get<{ items: PosSale[] }>('/pos/sales', { params: { sessionId: id, limit: 500 } })
      .then((r) => { if (on) setSales(r.data.items); })
      .catch(() => { if (on) setSales([]); });
    void api()
      .get<PosZReport>(`/pos/sessions/${id}/z-report`)
      .then((r) => { if (on) setZ(r.data); })
      .catch(() => { if (on) setZ('none'); });
    void api()
      .get<{ items: PosCollection[] }>(`/pos/sessions/${id}/collections`)
      .then((r) => { if (on) setCollections(r.data.items); })
      .catch(() => { if (on) setCollections([]); });
    return () => { on = false; };
  }, [id]);

  if (!session) return <Dialog open={false} onOpenChange={onClose}><DialogContent /></Dialog>;

  const ccy = session.currencyCode;
  const open = session.status === 'open';
  const when = (iso: string): string => formatDateTime(iso, { locale: i18n.language });
  const report = z && z !== 'none' ? z : null;

  // One card per currency the shift touched, from the Z-report. Without it (no
  // permission), a CLOSED shift still has its persisted per-currency counts.
  const cards = sessionDrawerCards(session, report);
  const breakdown = sessionBreakdown(session, report, collections);

  // How it was paid, in the shift's own currency (cash first, then the rest).
  const own = report?.byCurrency?.find((c) => c.currencyCode === ccy);
  const tenders = own?.byTender ?? report?.byTender ?? [];
  const cash = tenders.find((x) => x.kind === 'cash');
  // A sale on account takes no tender: what the tenders do not cover was sold unpaid.
  const ownOnAccount = own ? Number(own.netSales) - own.byTender.reduce((acc, x) => acc + Number(x.total), 0) : 0;
  const otherTenders = [
    ...tenders
      .filter((x) => x.kind !== 'cash' && Number(x.total) !== 0)
      .map((x) => `${t(`shifts.${x.kind}`, { defaultValue: x.kind })} ${money(x.total, ccy)}`),
    ...(ownOnAccount > 0.004 ? [`${t('shifts.credit', { defaultValue: 'On account' })} ${money(ownOnAccount, ccy)}`] : []),
  ].join(' · ');

  // The sales, and every customer who paid off their debt here.
  const rows = sales === null ? null : sessionSaleRows(sales, collections, t, money);

  const takings = report?.byCurrency?.length
    ? report.byCurrency.map((c) => ({ currencyCode: c.currencyCode, amount: c.netSales }))
    : (session.takings ?? []).map((x) => ({ currencyCode: x.currencyCode, amount: x.total }));
  const salesCount = report?.byCurrency?.length
    ? report.byCurrency.reduce((acc, c) => acc + c.saleCount, 0)
    : (session.salesCount ?? rows?.filter((r) => r.kind === 'sale').length ?? 0);

  const print = (): void => {
    printHtml(buildShiftPrintHtml({
      number: session.sessionNumber,
      cashier: session.cashierName ?? null,
      openedAt: session.openedAt,
      closedAt: session.closedAt,
      salesCount,
      shiftCurrency: ccy,
      breakdown,
      drawers: cards,
      note: session.closingNote,
      rows,
    }, t, {
      storeName: tenant?.name ?? '',
      locale: i18n.language,
      rtl: i18n.dir() === 'rtl',
      rates,
    }));
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {open
              ? t('shifts.openShiftTitle', { defaultValue: 'Shift in progress' })
              : t('shifts.reportTitle', { defaultValue: 'Register close-out' })}
            <span className="font-mono text-sm font-normal text-fg-muted">{session.sessionNumber}</span>
            {open && <Badge tone="info">{t('shifts.statusOpen', { defaultValue: 'Open' })}</Badge>}
          </DialogTitle>
          <DialogDescription>
            {session.cashierName ?? t('shifts.cashier', { defaultValue: 'Cashier' })}
            {' · '}{when(session.openedAt)} → {session.closedAt ? when(session.closedAt) : t('shifts.now', { defaultValue: 'now' })}
            {' · '}{duration(session.openedAt, session.closedAt)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[75vh] flex-col gap-4 overflow-auto pe-1">
          {z === null ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <>
              <ShiftTiles
                salesCount={salesCount}
                takings={takings}
                method={{
                  main: money(cash?.total ?? '0', ccy),
                  hint: otherTenders || t('shifts.cashOnly', { defaultValue: 'all cash' }),
                }}
                cards={cards.length ? cards : [{
                  // No figures to show (no Z-report, an open shift): a bare "open" card keeps the tiles honest.
                  currencyCode: ccy, float: session.openingCash, received: '0', fromOtherSales: null, paidInOtherNotes: null,
                  changeGiven: null, paidOut: null, paidOutLabel: 'refunds', expected: '0', counted: null, difference: null, takings: '0',
                }]}
                shiftCurrency={ccy}
                rates={rates}
                breakdown={breakdown}
              />
              <PaymentMethods breakdown={breakdown} />
              {cards.length > 0 && <DrawerCards cards={cards} />}
            </>
          )}
          <NoteBox note={session.closingNote} />
          <SalesByCurrency
            rows={rows}
            shiftCurrency={ccy}
            emptyText={t('shifts.salesNone', { defaultValue: 'No sales were rung up in this shift.' })}
          />
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>{tc('actions.close', { defaultValue: 'Close' })}</Button>
          <Button onClick={print} disabled={z === null || sales === null}>
            <Printer className="h-4 w-4" aria-hidden />{tc('actions.print', { defaultValue: 'Print' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
